import React from 'react';

/**
 * PeriodMonthPicker — the small "Period" month input used on the Apple
 * Scorecard and Employee Quadrants pages. Extracted verbatim from those pages
 * so the identical label + <input type="month"> block lives in one place.
 *
 * `value`/`onChange` are passed through unchanged so each call site keeps its
 * own state wiring. `className`/`labelClassName`/`htmlFor` default to the
 * existing markup, so nobody's appearance changes.
 */
interface PeriodMonthPickerProps {
  /** Current month value, e.g. "2026-06". */
  value: string;
  /** Change handler for the month input, passed through verbatim. */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Visible label text. Defaults to "Period". */
  label?: string;
  /** id of the input / htmlFor of the label. Defaults to "period". */
  htmlFor?: string;
  /** Wrapper classes. Defaults to the existing flex row. */
  className?: string;
  /** Label classes. Defaults to the existing styling. */
  labelClassName?: string;
  /** Input classes. Defaults to the existing styling. */
  inputClassName?: string;
}

const DEFAULT_LABEL_CLASS = 'text-xs font-medium text-gray-600';
const DEFAULT_INPUT_CLASS =
  'rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100';

export default function PeriodMonthPicker({
  value,
  onChange,
  label = 'Period',
  htmlFor = 'period',
  className = 'flex items-center gap-2',
  labelClassName = DEFAULT_LABEL_CLASS,
  inputClassName = DEFAULT_INPUT_CLASS,
}: PeriodMonthPickerProps) {
  return (
    <div className={className}>
      <label className={labelClassName} htmlFor={htmlFor}>{label}</label>
      <input
        id={htmlFor}
        type="month"
        className={inputClassName}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}
